import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronRight, ListFilter } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import {
  Button,
  Card,
  Combobox,
  Pagination,
  SearchInput,
  Skeleton,
  TBody,
  THead,
  Table,
  Tabs,
  TabsList,
  TabsTrigger,
  Td,
  Th,
  Tr,
} from "@rumbledirect/ui-v2";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { DossierTaskSheet } from "@/dossiers/DossierTaskSheet";
import { useOpenWorkQueueTask } from "@/dossiers/useOpenWorkQueueTask";
import type { GetWorkQueueTasksParams } from "@/lib/api/generated/model";
import { useGetWorkQueueTasks } from "@/lib/api/generated/work-queue/work-queue";
import { type Task, taskColumns } from "@/tasks/taskColumns";
import { taskDossierId } from "@/tasks/taskSubject";

/** Server page size. Kept within the endpoint maximum of 30. */
const ITEMS_PER_PAGE = 15;

type TaskTab = "mine" | "team" | "unassigned";

const TASK_TABS: readonly {
  readonly value: TaskTab;
  readonly label: string;
}[] = [
  { value: "mine", label: "Mijn taken" },
  { value: "team", label: "Mijn teams" },
  { value: "unassigned", label: "Niet toegewezen" },
];

function isTaskTab(value: unknown): value is TaskTab {
  return value === "mine" || value === "team" || value === "unassigned";
}

type TaskStatus = "action_required" | "automatic_follow_up" | "completed";

const TASK_STATUS_FILTERS: readonly {
  readonly value: TaskStatus;
  readonly label: string;
}[] = [
  { value: "action_required", label: "Actie vereist" },
  { value: "automatic_follow_up", label: "Automatische opvolging" },
  { value: "completed", label: "Afgeronde taken" },
];

function isTaskStatus(value: unknown): value is TaskStatus {
  return TASK_STATUS_FILTERS.some(({ value: status }) => status === value);
}

interface TasksSearch {
  readonly page: number;
  readonly tab: TaskTab;
  readonly status: TaskStatus;
  /** Free-text search term; undefined means unfiltered. */
  readonly query?: string;
  /** Id of the task shown in the detail sheet; undefined means no sheet. */
  readonly task?: string;
}

export const Route = createFileRoute("/_authenticated/tasks")({
  // Pagination, the active tab, the status filter, the search term and the
  // open task's sheet live in the URL so a view is shareable and survives
  // reloads.
  validateSearch: (search: Record<string, unknown>): TasksSearch => {
    const page = Number(search.page);
    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      tab: isTaskTab(search.tab) ? search.tab : "mine",
      status: isTaskStatus(search.status) ? search.status : "action_required",
      query: typeof search.query === "string" ? search.query : undefined,
      task: typeof search.task === "string" ? search.task : undefined,
    };
  },
  component: TasksPage,
});

/**
 * Maps a tab to its server-side assignee or team filter. "Mijn taken" is what
 * the user may act on: their own tasks plus the unassigned ones of their teams.
 * "Niet toegewezen" is work nobody owns yet: no assignee and no team.
 */
function tabFilters(tab: TaskTab, userId: string): GetWorkQueueTasksParams {
  switch (tab) {
    case "mine":
      return { actionableBy: userId };
    case "team":
      return { userCollectionMember: userId };
    case "unassigned":
      return { "exists[assignee]": false, "exists[userCollection]": false };
  }
}

/** Maps the status filter to its server-side completion/automation filter. */
function statusFilters(status: TaskStatus): GetWorkQueueTasksParams {
  switch (status) {
    case "action_required":
      return { "exists[completedAt]": false, pendingAutomation: false };
    case "automatic_follow_up":
      return { "exists[completedAt]": false, pendingAutomation: true };
    case "completed":
      return { "exists[completedAt]": true };
  }
}

const SKELETON_ROWS = 8;

const SKELETON_ROW_KEYS = Array.from(
  { length: SKELETON_ROWS },
  (_, index) => `task-skeleton-${index}`,
);

interface TasksRowsProps {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly Task[] | undefined;
  readonly onOpenTask: (task: Task) => void;
  readonly onRetry: () => void;
}

// Renders the table body for every state: loading skeletons, a recoverable
// error with retry, an empty state, or the task rows. Local to this route:
// the dashboard's Werkvoorraad card renders through its own row-grid pattern
// (see WorkQueueRows/@/lib/rowGrid) rather than this `<table>`-based one, so
// this has exactly one consumer.
function TasksRows({
  isPending,
  isError,
  members,
  onOpenTask,
  onRetry,
}: TasksRowsProps): ReactNode {
  const colSpan = taskColumns.length + 1;

  if (isError) {
    return (
      <Tr>
        <Td colSpan={colSpan} className="py-10 text-center">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              We konden de taken niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        {taskColumns.map((column) => (
          <Td key={column.id}>
            <Skeleton className="h-4 w-24" />
          </Td>
        ))}
        <Td />
      </Tr>
    ));
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td
          colSpan={colSpan}
          className="py-10 text-center text-muted-foreground"
        >
          Geen taken gevonden.
        </Td>
      </Tr>
    );
  }

  return members.map((task) => (
    <Tr key={task.id} className="group relative hover:bg-muted/40">
      {taskColumns.map((column) => (
        <Td key={column.id}>{column.cell(task)}</Td>
      ))}
      <Td className="text-right">
        {/* after:absolute after:inset-0 stretches this button across the whole
            row, so the entire row opens the sheet while remaining a single
            tab stop. Mirrors the dossiers list's stretched-link pattern. */}
        <button
          type="button"
          aria-label={`Taakdetails openen voor ${task.message.title}`}
          onClick={() => onOpenTask(task)}
          className="inline-flex text-muted-foreground transition-colors after:absolute after:inset-0 hover:text-foreground focus-visible:outline-none"
        >
          <ChevronRight className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </button>
      </Td>
    </Tr>
  ));
}

function TasksPage(): ReactNode {
  const { page, tab, status, query, task: openTaskId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const navigateToRoute = useNavigate();
  const currentUser = useCurrentUser();

  // Local search box synced to the URL with a small debounce so each keystroke
  // doesn't push a history entry / refetch. Mirrors the drivers list.
  const [searchInput, setSearchInput] = useState(query ?? "");
  useEffect(() => {
    setSearchInput(query ?? "");
  }, [query]);
  useEffect(() => {
    if (searchInput === (query ?? "")) return;
    const timer = window.setTimeout(() => {
      void navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          query: searchInput === "" ? undefined : searchInput,
        }),
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, query, navigate]);

  const { data, isPending, isError, refetch } = useGetWorkQueueTasks({
    page,
    itemsPerPage: ITEMS_PER_PAGE,
    search: query,
    ...tabFilters(tab, currentUser.userId),
    ...statusFilters(status),
  });

  // The sheet's task comes from the loaded list when it's on the current page;
  // only a deep link (or a task filtered out since) falls back to a detail
  // fetch inside the hook.
  const listedTask = data?.member.find((member) => member.id === openTaskId);
  const openTask = useOpenWorkQueueTask(openTaskId, listedTask);
  const openTaskDossierId = taskDossierId(openTask.task);

  const goToPage = (next: number): void => {
    void navigate({ search: (prev) => ({ ...prev, page: next }) });
  };

  const setTab = (next: string): void => {
    if (!isTaskTab(next)) return;
    void navigate({ search: (prev) => ({ ...prev, page: 1, tab: next }) });
  };

  const setStatus = (next: string): void => {
    if (!isTaskStatus(next)) return;
    void navigate({ search: (prev) => ({ ...prev, page: 1, status: next }) });
  };

  const openSheet = (taskId: string): void => {
    void navigate({ search: (prev) => ({ ...prev, task: taskId }) });
  };

  const closeSheet = (): void => {
    void navigate({ search: (prev) => ({ ...prev, task: undefined }) });
  };

  return (
    <>
      {/* Visually hidden; the AppShell topbar already shows the title visually. */}
      <h1 className="sr-only">Taken</h1>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TASK_TABS.map(({ value, label }) => (
              <TabsTrigger key={value} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <SearchInput
            className="w-80"
            value={searchInput}
            placeholder="Zoek taak, relatie of medewerker"
            onChange={(event) => setSearchInput(event.target.value)}
            onClear={() => setSearchInput("")}
          />
          <Combobox
            aria-label="Filter taken"
            value={status}
            onValueChange={setStatus}
            options={TASK_STATUS_FILTERS}
            placeholder="Alle taken"
            searchable={false}
            clearable={false}
            icon={<ListFilter className="size-4" />}
            triggerClassName="w-64"
          />
        </div>
      </div>

      <Card>
        <Table>
          <THead>
            <Tr className="bg-surface-muted/50">
              {taskColumns.map((column) => (
                <Th key={column.id}>{column.header}</Th>
              ))}
              <Th>
                <span className="sr-only">Openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            <TasksRows
              isPending={isPending}
              isError={isError}
              members={data?.member}
              onOpenTask={(task) => {
                if (task.id) openSheet(task.id);
              }}
              onRetry={() => {
                void refetch();
              }}
            />
          </TBody>
        </Table>
      </Card>

      <Pagination
        className="mt-4"
        page={page}
        pageSize={ITEMS_PER_PAGE}
        totalItems={data?.totalItems ?? 0}
        status={isPending ? "pending" : isError ? "error" : "ready"}
        onPageChange={goToPage}
      />

      <DossierTaskSheet
        task={openTask.task}
        isPending={openTask.isPending}
        isError={openTask.isError}
        onRetry={openTask.retry}
        dossierId={openTaskDossierId}
        onClose={closeSheet}
        onViewActivity={
          openTaskDossierId
            ? () => {
                // Navigating away from this route drops the `task` search
                // param, closing the sheet along the way.
                void navigateToRoute({
                  to: "/dossiers/$dossierId",
                  params: { dossierId: openTaskDossierId },
                  search: { tab: "activiteit", task: undefined },
                });
              }
            : undefined
        }
      />
    </>
  );
}
